import React, { useEffect, useRef, useState } from 'react';
import { Text, Billboard } from '@react-three/drei';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { TOWN_LOCATIONS, ENTRY_VIA_PORTAL } from '../rooms';
import { useVenueAnchors } from '../hooks/useVenueAnchors';

// A clickable proxy over a venue's whole building: click anywhere on it to
// enter, and it glows on hover so it's clearly interactive.
//
// This IS the "entire building is the hit zone" that ENTRY_VIA_PORTAL retires —
// a box the size of the venue that opens it from any face, roof included. With
// doors on it draws nothing at all rather than staying as a dead hover glow,
// which would advertise an interaction that no longer happens.
function VenueBuilding({ box, accent, interiorId }) {
  const enterInterior = useWorldStore((s) => s.enterInterior);
  const [hover, setHover] = useState(false);
  if (!interiorId || ENTRY_VIA_PORTAL) return null;
  return (
    <group position={box.center}>
      <mesh
        onClick={(e) => { e.stopPropagation(); enterInterior(interiorId); }}
        onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = 'auto'; }}
      >
        <boxGeometry args={box.size} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {hover && (
        <mesh scale={[1.02, 1.02, 1.02]}>
          <boxGeometry args={box.size} />
          <meshBasicMaterial color={accent} transparent opacity={0.18} depthWrite={false} side={THREE.BackSide} blending={THREE.AdditiveBlending} toneMapped={false} />
        </mesh>
      )}
    </group>
  );
}

// VenueMarkers — a small billboarded sign hovering just above each venue's
// building (anchor y = the building's measured roof height), so it reads as
// "this building" without being lost high in the sky. Click the disc to step
// inside. Venues are spread across the city (see CityModel), so signs don't
// overlap.

const ROOF_MARGIN = 3; // sit this far above the building's roofline

function Beacon({ pos, name, accent, interiorId }) {
  const MARK_Y = pos[1] + ROOF_MARGIN;
  const enterInterior = useWorldStore((s) => s.enterInterior);
  const disc = useRef();
  // STATIC ON PURPOSE (Khari, 2026-08-02: "the markers on the building are
  // pulsing.. wasted compute"). The sine pulse ran a useFrame + material write
  // per marker per frame for an effect nobody needed — a beacon reads as a
  // beacon at a fixed opacity. If it ever comes back, it should be a shader
  // uniform on a shared material, not per-marker JS.
  useEffect(() => {
    if (disc.current) disc.current.material.opacity = 0.85;
  }, []);
  // THE SIGN STAYS A WAY IN, deliberately, and is the one exception to
  // ENTRY_VIA_PORTAL.
  //
  // It is not a facade. A marker is wayfinding — the same category as the map,
  // which also still enters — and it is explicitly presented as UI rather than
  // as part of the city. What that rule retires is the pretence that a whole
  // building is a button: a box you can click on the roof, through a wall, or
  // from the far side of the block, so a venue has no front and nothing can be
  // stationed at it.
  //
  // Nothing is bypassed by keeping it. The check lives in enterInterior, so a
  // marker click into a gated venue is refused exactly like walking into the
  // door — which is the whole reason the gate was put in the store rather than
  // on the door the guard happens to stand next to.
  const enter = interiorId ? (e) => { e.stopPropagation(); enterInterior(interiorId); } : undefined;
  const hover = interiorId
    ? { onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; },
        onPointerOut: () => { document.body.style.cursor = 'auto'; } }
    : {};
  return (
    <group position={[pos[0], 0, pos[2]]}>
      {/* short drop-pin to the pavement below the sign */}
      <mesh position={[0, MARK_Y / 2, 0]}>
        <cylinderGeometry args={[0.08, 0.08, MARK_Y, 6]} />
        <meshBasicMaterial color={accent} toneMapped={false} transparent opacity={0.5} />
      </mesh>
      <Billboard position={[0, MARK_Y, 0]}>
        <mesh ref={disc} onClick={enter} {...hover}>
          <circleGeometry args={[1.1, 32]} />
          <meshBasicMaterial color={accent} transparent opacity={0.9} depthWrite={false} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, 0.01]}>
          <ringGeometry args={[1.1, 1.35, 32]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.9} toneMapped={false} />
        </mesh>
        <Text position={[0, 1.9, 0]} fontSize={1.5} color="#ffffff" outlineWidth={0.09} outlineColor="#000000" anchorX="center" anchorY="middle">
          {name}
        </Text>
      </Billboard>
      <pointLight position={[0, MARK_Y, 0]} color={accent} intensity={4} distance={12} />
    </group>
  );
}

export default function VenueMarkers() {
  // Through the shared hook so an unsaved placement moves the marker too.
  const venueAnchors = useVenueAnchors();
  const venueBoxes = useWorldStore((s) => s.venueBoxes);
  if (!venueAnchors) return null;
  return (
    <group>
      {TOWN_LOCATIONS.map((loc) => {
        const a = venueAnchors[loc.key];
        if (!a) return null;
        return (
          <group key={loc.key}>
            <Beacon pos={a} name={loc.name} accent={loc.accent} interiorId={loc.interiorId} />
            {venueBoxes && venueBoxes[loc.key] && (
              <VenueBuilding box={venueBoxes[loc.key]} accent={loc.accent} interiorId={loc.interiorId} />
            )}
          </group>
        );
      })}
    </group>
  );
}
